import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { HARM_TYPES } from "@/lib/constants";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Target, Buildings, FileText, Check } from "@phosphor-icons/react";

const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/65306284-f0b7-4b09-9326-a0975e697622/images/f247fcede9b537cb41f168dbba7e0020704ecb25bb278c008bc2751920bcba9c.png";

export default function NewCase() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    title: "",
    corporation: "",
    harm_type: "",
    description: "",
    estimated_damages: "",
    incident_date: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return form.corporation.trim() && form.title.trim();
    if (step === 2) return form.harm_type;
    if (step === 3) return form.description.trim().length >= 10;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        estimated_damages: parseFloat(form.estimated_damages || "0") || 0,
        incident_date: form.incident_date || null,
      };
      const { data } = await api.post("/cases", payload);
      toast.success("Case deployed. Generating intelligence...");
      navigate(`/cases/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create case");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { n: "01", t: "Target", i: Buildings },
    { n: "02", t: "Harm", i: Target },
    { n: "03", t: "Narrative", i: FileText },
    { n: "04", t: "Confirm", i: Check },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white relative" data-testid="new-case-page">
      <Nav />
      <div className="absolute inset-0 bg-cover bg-center opacity-5 pointer-events-none" style={{ backgroundImage: `url(${TEXTURE_BG})` }} />

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 font-mono-sf text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-[#FF3366] mb-8 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft size={14} weight="bold" /> Back to Cases
        </Link>

        <div className="font-mono-sf text-xs uppercase tracking-[0.3em] text-[#FF3366] mb-3">// new operation</div>
        <h1 className="font-heading font-black uppercase tracking-tighter text-5xl md:text-6xl mb-10">
          Deploy a case.
        </h1>

        {/* Steps */}
        <div className="grid grid-cols-4 border border-[#1A1A1A] divide-x divide-[#1A1A1A] mb-10" data-testid="wizard-steps">
          {steps.map((s, i) => {
            const Icon = s.i;
            const active = step === i + 1;
            const done = step > i + 1;
            return (
              <div key={s.n} className={`p-4 md:p-5 ${active ? "bg-[#FF3366] text-white" : done ? "bg-[#1A1A1A]" : "bg-[#0D0D0D] text-neutral-500"}`} data-testid={`step-${i+1}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-sf text-[10px] tracking-[0.25em]">{s.n}</span>
                  <Icon size={14} weight="bold" />
                </div>
                <div className="font-heading font-bold uppercase tracking-tight text-sm md:text-base">{s.t}</div>
              </div>
            );
          })}
        </div>

        <div className="border border-[#1A1A1A] bg-[#0D0D0D]/80 backdrop-blur p-6 md:p-10">
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1-content">
              <h2 className="font-heading font-bold uppercase tracking-tight text-2xl mb-1">Identify the adversary.</h2>
              <p className="text-neutral-400 text-sm mb-6">Name the corporation and a short case label.</p>

              <div>
                <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Case Title</label>
                <input
                  value={form.title}
                  onChange={(e) => update("title", e.target.value)}
                  placeholder="Wrongful Equifax credit reporting"
                  className="w-full h-12 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none px-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm"
                  data-testid="case-title-input"
                />
              </div>
              <div>
                <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Adversary Corporation</label>
                <input
                  value={form.corporation}
                  onChange={(e) => update("corporation", e.target.value)}
                  placeholder="Equifax Inc."
                  className="w-full h-12 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none px-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm"
                  data-testid="case-corporation-input"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-2-content">
              <h2 className="font-heading font-bold uppercase tracking-tight text-2xl mb-1">Classify the harm.</h2>
              <p className="text-neutral-400 text-sm mb-6">Pick the violation type — this drives the AI's legal framework.</p>
              <div className="grid sm:grid-cols-2 gap-px bg-[#1A1A1A] border border-[#1A1A1A]">
                {HARM_TYPES.map((h) => {
                  const sel = form.harm_type === h.value;
                  return (
                    <button
                      key={h.value}
                      type="button"
                      onClick={() => update("harm_type", h.value)}
                      className={`p-4 text-left transition-colors ${sel ? "bg-[#FF3366] text-white" : "bg-[#0D0D0D] hover:bg-[#1A1A1A]"}`}
                      data-testid={`harm-${h.value}`}
                    >
                      <div className="font-heading font-bold uppercase tracking-tight text-sm">{h.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-3-content">
              <h2 className="font-heading font-bold uppercase tracking-tight text-2xl mb-1">Tell the story.</h2>
              <p className="text-neutral-400 text-sm mb-6">Facts only — dates, dollar amounts, communications. The AI uses this verbatim.</p>

              <div>
                <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">What happened? (min 10 chars)</label>
                <textarea
                  rows={9}
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  placeholder="On 2026-01-12 Equifax added a $4,200 charge-off to my credit report from an account I never opened. I disputed twice (1/14, 2/02) — both rejected within 24 hours by their automated system without investigation. My mortgage application was denied 2/06 as a result..."
                  className="w-full bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none p-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm leading-relaxed"
                  data-testid="case-description-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Estimated Damages (USD)</label>
                  <input
                    type="number"
                    min="0"
                    value={form.estimated_damages}
                    onChange={(e) => update("estimated_damages", e.target.value)}
                    placeholder="4200"
                    className="w-full h-12 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none px-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm"
                    data-testid="case-damages-input"
                  />
                </div>
                <div>
                  <label className="block font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Incident Date</label>
                  <input
                    type="date"
                    value={form.incident_date}
                    onChange={(e) => update("incident_date", e.target.value)}
                    className="w-full h-12 bg-[#0D0D0D] border border-[#1A1A1A] focus:border-[#FF3366] focus:ring-2 focus:ring-[#FF3366] focus:outline-none px-4 text-white placeholder:text-neutral-600 font-mono-sf text-sm"
                    data-testid="case-date-input"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="step-4-content">
              <h2 className="font-heading font-bold uppercase tracking-tight text-2xl mb-1">Confirm and deploy.</h2>
              <p className="text-neutral-400 text-sm mb-6">Review the operation brief.</p>

              <div className="border border-[#1A1A1A] divide-y divide-[#1A1A1A]">
                {[
                  ["Title", form.title],
                  ["Adversary", form.corporation],
                  ["Harm", HARM_TYPES.find((h) => h.value === form.harm_type)?.label],
                  ["Damages", `$${parseFloat(form.estimated_damages || "0").toLocaleString()}`],
                  ["Incident Date", form.incident_date || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 p-4">
                    <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500">{k}</div>
                    <div className="col-span-2 font-mono-sf text-sm">{v || "—"}</div>
                  </div>
                ))}
                <div className="p-4">
                  <div className="font-mono-sf text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Narrative</div>
                  <div className="font-mono-sf text-sm text-neutral-300 whitespace-pre-wrap max-h-48 overflow-y-auto">{form.description}</div>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-between mt-10 pt-6 border-t border-[#1A1A1A]">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="h-11 px-5 border border-[#1A1A1A] hover:bg-[#1A1A1A] disabled:opacity-30 font-mono-sf text-xs uppercase tracking-[0.25em] flex items-center gap-2 transition-colors"
              data-testid="wizard-back-btn"
            >
              <ArrowLeft size={14} weight="bold" /> Back
            </button>
            {step < 4 ? (
              <button
                type="button"
                onClick={() => canNext() && setStep((s) => s + 1)}
                disabled={!canNext()}
                className="h-11 px-6 bg-[#FF3366] hover:bg-[#FF5248] disabled:opacity-30 text-white font-mono-sf text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2 transition-colors"
                data-testid="wizard-next-btn"
              >
                Next <ArrowRight size={14} weight="bold" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="h-11 px-6 bg-[#FF3366] hover:bg-[#FF5248] disabled:opacity-50 text-white font-mono-sf text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2 transition-colors"
                data-testid="wizard-deploy-btn"
              >
                {submitting ? "Deploying..." : <>Deploy Case <Check size={14} weight="bold" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
